// WHAT: Fullscreen chat component for Ollama conversations
// WHY:  Provides distraction-free fullscreen interface for AI chat
// DOMAIN: LLM → Ollama
// DEPENDENCIES: ChatWindow, ModelSelector, useOllamaChat hook
// RISKS:
//   - NOTE: Fullscreen mode may not work correctly on all screen sizes
//   - NOTE: Model selector integration may cause re-renders
//   - NOTE: Close button may not be accessible in all scenarios

import { X, Bot } from "lucide-react";
import { type FC } from "react";

import { Button, Stack, Heading } from "@/components/ui";

import { useOllamaChat } from "../hooks/useOllamaChat";
import { ChatWindow } from "./ChatWindow";
import { ModelSelector } from "./ModelSelector";

/**
 * Props for OllamaFullscreenChat component
 */
interface OllamaFullscreenChatProperties {
  /** Close handler */
  onClose: () => void;
}

/**
 * WHAT:
 *   Fullscreen chat component that takes over the entire viewport for
 *   distraction-free Ollama conversations.
 *
 * WHY:
 *   Enables users to focus on AI conversations without UI distractions.
 *
 * BEHAVIOR:
 *   - Displays header with model selector and close button
 *   - Shows fullscreen chat window
 *   - Provides model switching capability
 *
 * RISKS:
 *   - NOTE: Fullscreen mode may not work correctly on all screen sizes
 *   - NOTE: Model selector integration may cause re-renders
 *   - NOTE: Close button may not be accessible in all scenarios
 *
 * @param root0
 *
 * @param root0.onClose
 *
 * @returns JSX element with fullscreen chat interface
 *
 */
export const OllamaFullscreenChat: FC<OllamaFullscreenChatProperties> = ({ onClose }) => {
  const { currentModel, setModel } = useOllamaChat();

  return (
    <div className="fixed inset-0 z-50 bg-slate-900 flex flex-col">
      {/* Header Bar */}
      <header className="h-16 border-b border-slate-700 flex items-center justify-between px-6 bg-slate-800/50 backdrop-blur-sm">
        <Stack align="center" direction="row" gap={3}>
          <Bot className="w-6 h-6 text-blue-400" />
          <Heading className="text-lg font-semibold text-white" level={3}>
            Ollama Chat
          </Heading>
          <div className="h-4 w-px bg-slate-700" />
          <ModelSelector currentModel={currentModel} onModelChange={setModel} />
        </Stack>

        <Button className="gap-2" onClick={onClose} size="sm" variant="ghost">
          <X className="w-4 h-4" />
          Close
        </Button>
      </header>

      {/* Full Screen Chat */}
      <div className="flex-1 overflow-hidden">
        <ChatWindow />
      </div>
    </div>
  );
};
